import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './index.css';

const Terms: React.FC = () => {
  useEffect(() => {
    document.title = 'Terms & Privacy — Lovestoria';
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <main className="terms">
      <header className="terms__head">
        <Link to="/" className="terms__brand">lovestoria</Link>
      </header>

      <article className="terms__body">
        <h1 className="terms__title">Terms &amp; Privacy</h1>

        <nav className="terms__toc" aria-label="Sections">
          <a href="#offer">Public Offer</a>
          <a href="#privacy">Privacy Policy</a>
          <a href="#use">Terms of Use</a>
        </nav>

        {/* ────────────────── PUBLIC OFFER ────────────────── */}
        <section id="offer" className="terms__section">
          <h2>Public Offer</h2>

          <h3>1. General provisions</h3>
          <p>
            1.1. This document is a public offer of Unsolved Solutions LLP
            (hereinafter the &laquo;Provider&raquo;) addressed to an indefinite
            number of persons (hereinafter the &laquo;Customer&raquo;) and contains
            all the essential terms of the contract for the provision of services
            for the creation of a personalized book in accordance with Articles
            395&ndash;397 of the Civil Code of the Republic of Kazakhstan.
          </p>
          <p>
            1.2. Acceptance of this offer is the payment by the Customer for the
            Provider&rsquo;s services by any of the methods indicated on the Site.
            The moment of payment is considered the moment of conclusion of the
            contract between the Provider and the Customer.
          </p>
          <p>
            1.3. The offer is valid from the moment of its publication on
            lovestoria.org and remains in force until withdrawn by the Provider.
          </p>
          <p>
            1.4. The Provider may amend the terms of the offer. Amendments take
            effect upon publication of the new version on the Site. Orders paid
            for prior to the effective date of the amendments are fulfilled on the
            previous terms.
          </p>

          <h3>2. Definitions</h3>
          <p>
            2.1. <b>Site</b> &mdash; the internet resource located at lovestoria.org.
          </p>
          <p>
            2.2. <b>Personalized Book</b> &mdash; a printed edition created on the
            basis of the answers, photographs and preferences of the Customer,
            constituting a unique copy.
          </p>
          <p>
            2.3. <b>Personal Account</b> &mdash; a protected section of the Site
            granted to the Customer after payment for the &laquo;Account
            creation&raquo; service, intended for filling in the materials of the
            book.
          </p>

          <h3>3. Subject of the contract</h3>
          <p>
            3.1. The Provider undertakes to provide the Customer with services for
            the creation of a personalized book and its delivery, and the
            Customer undertakes to pay for the said services in the order and on
            the terms provided for by this offer.
          </p>
          <p>3.2. The Provider&rsquo;s services include the following stages:</p>
          <ul>
            <li>
              <b>Account creation</b> &mdash; granting the Customer access to the
              Personal Account for filling in the book materials (answering
              questions, uploading photographs, choosing a cover).
            </li>
            <li>
              <b>Editing</b> &mdash; proofreading, stylistic processing, layout and
              preparation of the book.
            </li>
            <li>
              <b>Printing</b> &mdash; production of the unique copy of the book.
            </li>
            <li>
              <b>Delivery</b> &mdash; shipment of the finished book to the
              Customer via courier services.
            </li>
          </ul>

          <h3>4. Cost of services and payment procedure</h3>
          <p>
            4.1. The cost of services is shown on the Site at the moment of order
            placement. Payment plans (full payment, 50% deposit, 25% deposit) are
            offered at checkout.
          </p>
          <p>
            4.2. The cost of delivery is not included in the price of services
            and is calculated separately based on the delivery address and
            courier rates, unless otherwise stated at checkout.
          </p>
          <p>
            4.3. Prices on the Site are listed in the local currency of the
            Customer where supported, or in US dollars (USD). When paying in a
            different currency the final amount may differ from the price shown
            on the Site due to exchange-rate fluctuations and payment-processor
            fees.
          </p>
          <p>
            4.4. If the Customer chose a deposit plan (50% or 25%), the
            remaining balance becomes due before the book is sent to print and is
            requested from the Customer via the same payment method.
          </p>

          <h3>5. Procedure for providing services</h3>
          <p>
            5.1. After payment, the Customer is granted access to the Personal
            Account (login and password). The &laquo;Account creation&raquo;
            service is considered rendered at the moment access is granted.
          </p>
          <p>
            5.2. The Customer fills in the book materials in the Personal
            Account: answers questions, uploads photographs, chooses the cover
            design.
          </p>
          <p>
            5.3. Upon completion of filling in, the Customer presses the
            &laquo;Finish&raquo; button (or otherwise notifies that the material
            is ready). From this moment the editing process begins.
          </p>
          <p>
            5.4. The order fulfilment period (editing, printing, transfer to the
            courier service) is up to 21 (twenty-one) business days from the
            moment the Customer presses the &laquo;Finish&raquo; button.
          </p>
          <p>
            5.5. The contents of the book are visible only to the Customer and
            the Provider&rsquo;s editor. The Customer may decline editing, in
            which case the material is published as authored.
          </p>

          <h3>6. Delivery</h3>
          <p>
            6.1. Delivery is carried out by courier services to the address
            specified by the Customer when placing the order.
          </p>
          <p>
            6.2. The cost of delivery is determined by the courier&rsquo;s rates
            and is paid by the Customer additionally.
          </p>
          <p>
            6.3. Delivery times depend on the region and on the courier&rsquo;s
            terms. The Provider is not liable for delays caused by the courier
            service.
          </p>
          <p>
            6.4. The risk of accidental loss or damage to the book passes to the
            Customer at the moment the book is handed over to the courier
            service.
          </p>

          <h3>7. Returns and refusal of services</h3>
          <p>
            7.1. The cost of the &laquo;Account creation&raquo; portion of the
            service is non-refundable, since access is created individually and
            allows the Customer to begin work on the book immediately.
          </p>
          <p>
            7.2. If the Customer requests a refund before the editing process
            starts, the cost of &laquo;Editing&raquo; and &laquo;Printing&raquo;
            is refunded in full.
          </p>
          <p>
            7.3. The editing process is considered started at the moment the
            Customer presses the &laquo;Finish&raquo; button or otherwise
            notifies that the material is ready. From this moment the cost of
            the &laquo;Editing&raquo; service is non-refundable.
          </p>
          <p>
            7.4. If, after the editing has begun, the Customer refuses printing,
            the refund is issued only for the &laquo;Printing&raquo; portion of
            the service.
          </p>
          <p>
            7.5. To request a refund the Customer contacts the Provider by any
            available means (phone, email, messengers) indicating the login,
            date of purchase and reason for the refund. The refund is issued
            within 14 (fourteen) calendar days of approval of the request.
          </p>

          <h3>8. Rights and obligations of the parties</h3>
          <p><b>The Provider is obliged to:</b></p>
          <ul>
            <li>provide services of proper quality within the established time;</li>
            <li>
              ensure the confidentiality of the Customer&rsquo;s personal data
              and materials;
            </li>
            <li>
              notify the Customer of the readiness of the order and its transfer
              to the courier service.
            </li>
          </ul>
          <p><b>The Provider has the right to:</b></p>
          <ul>
            <li>
              unilaterally amend the terms of the offer, with publication of
              changes on the Site;
            </li>
            <li>
              engage third parties to perform individual stages of the services
              (printing, delivery).
            </li>
          </ul>
          <p><b>The Customer is obliged to:</b></p>
          <ul>
            <li>provide accurate data for the creation of the book and for delivery;</li>
            <li>pay for the services in full;</li>
            <li>
              not use the Site to publish illegal, offensive or third-party
              rights infringing content;
            </li>
            <li>maintain the confidentiality of their account credentials.</li>
          </ul>
          <p><b>The Customer has the right to:</b></p>
          <ul>
            <li>request information about the order status;</li>
            <li>decline editing of the material;</li>
            <li>request a refund in accordance with section 7 of this offer.</li>
          </ul>

          <h3>9. Liability of the parties</h3>
          <p>
            9.1. The Provider applies all reasonable efforts to protect the
            Customer&rsquo;s personal data, but is not liable for data leakage
            resulting from unauthorised access by third parties.
          </p>
          <p>
            9.2. The Provider is not liable for the content of materials supplied
            by the Customer. Responsibility for the accuracy and lawfulness of
            the materials lies with the Customer.
          </p>
          <p>
            9.3. The Customer is responsible for all actions performed under
            their account credentials.
          </p>

          <h3>10. Personal data</h3>
          <p>
            10.1. By accepting this offer the Customer consents to the
            collection and processing of personal data to the extent necessary
            to perform obligations under this contract, in accordance with the
            Law of the Republic of Kazakhstan &laquo;On personal data and their
            protection&raquo;.
          </p>
          <p>
            10.2. The procedure for collection, storage and protection of
            personal data is defined by the Privacy Policy below.
          </p>
          <p>
            10.3. The Customer may at any time request access to their personal
            data, its correction, deletion or restriction of processing.
          </p>

          <h3>11. Intellectual property</h3>
          <p>
            11.1. All materials of the Site (texts, graphics, logos, images,
            design) are the property of the Provider and are protected by the
            copyright legislation of the Republic of Kazakhstan.
          </p>
          <p>
            11.2. Materials provided by the Customer (texts, photographs)
            remain the Customer&rsquo;s property. The Provider uses them solely
            to fulfil the order.
          </p>

          <h3>12. Force majeure</h3>
          <p>
            12.1. The parties are released from liability for failure to perform
            or improper performance of obligations if this is the result of
            force majeure: natural disasters, military actions, acts of
            governmental authorities, mass internet outages and other
            circumstances beyond the parties&rsquo; control.
          </p>

          <h3>13. Dispute resolution</h3>
          <p>
            13.1. All disputes and disagreements are resolved through
            negotiations. Failing agreement, the dispute is subject to
            consideration in the court at the location of the Provider in
            accordance with the legislation of the Republic of Kazakhstan.
          </p>

          <h3>14. Final provisions</h3>
          <p>14.1. This offer is governed by the legislation of the Republic of Kazakhstan.</p>
          <p>
            14.2. In all matters not provided for by this offer, the parties are
            governed by the applicable legislation of the Republic of Kazakhstan.
          </p>
          <p>
            14.3. Invalidity of an individual provision of the offer does not
            entail invalidity of the remaining provisions.
          </p>

          <h3>15. Provider details</h3>
          <p>
            Unsolved Solutions LLP<br />
            BIN: 260440017154<br />
            Registered address: Almaty, Republic of Kazakhstan<br />
            Email: support@lovestoria.org<br />
            Site: lovestoria.org
          </p>
        </section>

        {/* ────────────────── PRIVACY POLICY ────────────────── */}
        <section id="privacy" className="terms__section">
          <h2>Privacy Policy</h2>

          <h3>1. Introduction</h3>
          <p>
            We, Lovestoria (legal entity Unsolved Solutions LLP), are committed
            to protecting the privacy of your personal data. This privacy policy
            explains how we collect, use and protect information about users of
            our site.
          </p>

          <h3>2. Data we collect</h3>
          <ul>
            <li>
              <b>Personal data:</b> name, email address and other contact
              details that you provide when creating an account.
            </li>
            <li>
              <b>Answers to questions:</b> the information you supply when
              answering questions to create the personalized book.
            </li>
            <li>
              <b>Photographs:</b> images that you upload for inclusion in the book.
            </li>
            <li>
              <b>Cover preferences:</b> your preferences regarding the design of
              the book&rsquo;s cover.
            </li>
          </ul>

          <h3>3. Use of data</h3>
          <p>We use the collected data to:</p>
          <ul>
            <li>create your personalized book;</li>
            <li>process your orders and manage your account;</li>
            <li>improve our site and services.</li>
          </ul>

          <h3>4. Confidentiality, editing and disclosure</h3>
          <p>
            The contents of your book are visible only to you and our editor.
            You may decline editing, in which case your material remains entirely
            anonymous to us.
          </p>

          <h3>5. Disclaimer and data security</h3>
          <p>
            We apply every effort to protect your data, but cannot guarantee
            absolute security on the internet. In the event of a breach of our
            site we are not liable for leakage of your personal data. We employ
            various security measures to protect personal data, including
            electronic, physical and managerial procedures.
          </p>

          <h3>6. Your rights</h3>
          <p>
            You have the right at any time to request access to your personal
            data, its correction, deletion or restriction of processing. You
            also have the right to object to the processing of your data and to
            request its transfer.
          </p>

          <h3>7. Changes to the privacy policy</h3>
          <p>
            We may update our privacy policy from time to time. We will notify
            you of any changes by posting the new version on this page.
          </p>

          <h3>8. Contact information</h3>
          <p>
            If you have questions regarding this privacy policy you can contact
            us by email at support@lovestoria.org.
          </p>
        </section>

        {/* ────────────────── TERMS OF USE ────────────────── */}
        <section id="use" className="terms__section">
          <h2>Terms of Use</h2>

          <h3>1. General provisions</h3>
          <p>
            This policy defines the rules for using the site lovestoria.org,
            owned by Unsolved Solutions LLP. By using the site you confirm your
            agreement to these rules. If you do not agree with any of the
            conditions, please stop using the site.
          </p>
          <p>
            The terms for the provision of services, their cost and the refund
            procedure are defined by the Public Offer above.
          </p>

          <h3>2. Copyright and intellectual property</h3>
          <p>
            All materials on the site, including text, graphics, logos, icons,
            images, audio and video clips, are the property of Unsolved Solutions
            LLP or its content suppliers and are protected by the copyright
            legislation of the Republic of Kazakhstan. No materials of the site
            may be copied, reproduced, transmitted or distributed without the
            prior written permission of Unsolved Solutions LLP.
          </p>

          <h3>3. License of use</h3>
          <p>
            Unsolved Solutions LLP grants you a limited license to access and
            personally use the site. This license does not include:
          </p>
          <ul>
            <li>resale or commercial use of the site or its content;</li>
            <li>
              collection and use of any catalogues, product descriptions or
              prices;
            </li>
            <li>derivative use of the site or its content;</li>
            <li>downloading or copying information for the benefit of a third party;</li>
            <li>any use of automated data-collection or extraction tools.</li>
          </ul>

          <h3>4. Your account</h3>
          <p>
            If you use the site, you are responsible for maintaining the
            confidentiality of your account and password, and for restricting
            access to your device. You agree to accept responsibility for all
            actions performed under your account.
          </p>

          <h3>5. User content</h3>
          <p>
            By submitting materials to the site (texts, photographs) you confirm
            that you have the necessary rights to those materials. It is
            prohibited to submit content that is illegal, offensive, infringes
            third-party rights or contains malicious software.
          </p>

          <h3>6. Changes to the terms of use</h3>
          <p>
            We may make changes to these terms of use; we undertake to notify
            users by publishing the updated version on the site no later than 2
            calendar days before the changes take effect. Continued use of the
            site after the changes take effect constitutes agreement to the new
            terms.
          </p>

          <h3>7. Contact information</h3>
          <p>
            For questions related to use of the site, contact us by email at
            support@lovestoria.org.
          </p>
        </section>

        <footer className="terms__bottom">
          © 2026 Unsolved Solutions LLP · BIN 260440017154 · Almaty, Kazakhstan
        </footer>
      </article>
    </main>
  );
};

export default Terms;
